import { ChevronRight, Menu, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useMatches } from "react-router";
import { CommandPalette } from "@/shared/components/CommandPalette";
import { workspaceForPath } from "@/shared/constants/nav";
import { Button } from "@/shared/ui/button";
import { Icon } from "@/shared/ui/icon";
import { InviteIndicator } from "./InviteIndicator";
import { LiveIndicator } from "./LiveIndicator";

interface RouteHandle {
  title: string;
}

function isRouteHandle(h: unknown): h is RouteHandle {
  return typeof h === "object" && h !== null && typeof (h as RouteHandle).title === "string";
}

export function usePageTitle(): string {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const handle = matches[i]?.handle;
    if (isRouteHandle(handle)) return handle.title;
  }
  return "NexBorg Access";
}

interface TopBarProps {
  onOpenMenu: () => void;
  onlineCount?: number;
  /**
   * Terminals paired at all. The board's chip is "Live · 1 of 2 terminals online", and the
   * denominator is the half that makes it an answer: "1 terminal online" is a fact about one
   * terminal, "1 of 2" is a fact about the site. The shell already counts both off the devices
   * query it makes for the chip, so this costs nothing — it was simply never handed on.
   */
  totalCount?: number;
}

/**
 * **The bar on the ground** (shell refresh, 2026-09-22; pass 01 for what it carries).
 *
 * `--bar-h-page` tall, on `--surface` with one hairline under it, and it carries only what is global
 * and momentary: where you are, as a breadcrumb; the way to anywhere, as the ⌘K search at its
 * centre; the live channel; and invitations. Theme, organisation and account live at the foot of the
 * sidebar with the rest of "who and where" (`Sidebar`, `UserMenu`), so the right of this bar is
 * status, not identity.
 */
export function TopBar({ onOpenMenu, onlineCount, totalCount }: TopBarProps) {
  const title = usePageTitle();
  const { pathname } = useLocation();
  const workspace = workspaceForPath(pathname);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // ⌘K / Ctrl-K anywhere that is not already taking text. The palette is the way to every screen,
  // so it must not depend on reaching a control with the mouse first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "k" || !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      setPaletteOpen((open) => !open);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="flex h-(--bar-h-page) shrink-0 items-center gap-3 border-b border-border bg-surface px-(--canvas-gutter) lg:pl-0">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onOpenMenu}
        aria-label="Open navigation"
      >
        <Icon icon={Menu} size={20} />
      </Button>
      {/* Workspace › page, always — the workspace icon and name, then the screen. */}
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5">
        <Icon icon={workspace.icon} size={16} className="shrink-0 text-fg-muted" />
        <span className="shrink-0 text-cell text-fg-muted">{workspace.label}</span>
        <Icon icon={ChevronRight} size={16} className="size-3.5 shrink-0 text-fg-faint" />
        <span aria-current="page" className="min-w-0 truncate text-cell font-medium text-fg">
          {title}
        </span>
      </nav>
      {/*
        The search is the bar's centre of gravity, not a button in the corner: a raised 34 px field,
        centred, that says what it searches. A reader who has never opened the palette learns what it
        is for by reading it.
      */}
      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        aria-keyshortcuts="Meta+K Control+K"
        className="mx-auto hidden h-8 w-[440px] items-center gap-2.5 rounded-md border border-border bg-surface-sunken pl-3 pr-1.5 text-left transition-colors duration-120 hover:border-border-strong md:flex"
      >
        <Icon icon={Search} size={16} className="shrink-0 text-fg-faint" />
        <span className="truncate text-cell text-fg-muted">
          Search people, devices, doors, events, screens
        </span>
        <kbd className="ml-auto shrink-0 rounded-sm border border-border bg-surface px-1.5 font-mono text-caption font-medium text-fg-faint">
          ⌘K
        </kbd>
      </button>
      <div className="ml-auto flex items-center gap-2">
        <LiveIndicator
          {...(onlineCount !== undefined ? { onlineCount } : {})}
          {...(totalCount !== undefined ? { totalCount } : {})}
        />
        <InviteIndicator />
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </header>
  );
}
